import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  RefreshCw,
  Settings,
  Users,
  Phone,
  GitFork,
  Zap,
  Columns,
  BarChart2,
  AlertTriangle,
  Bot,
  Shield,
  ScrollText,
  HardDrive,
  CalendarDays,
  Receipt,
  Wallet,
  TrendingUp,
  Mail,
} from "lucide-react";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import useBillingStatus from "../hooks/useBillingStatus";
import { cn } from "../lib/utils";

const NavItem = ({ to, icon: Icon, label, collapsed, badge }) => {
  const location = useLocation();
  const active =
    location.pathname === to ||
    (to !== "/" && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg mx-2 transition-all duration-150 group relative",
        active
          ? "bg-indigo-600 text-white shadow-sm"
          : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
      )}
      title={collapsed ? label : undefined}
    >
      <span className="relative flex-shrink-0">
        <Icon
          className={cn(
            "w-5 h-5 transition-colors",
            active ? "text-white" : "text-slate-400 group-hover:text-slate-100"
          )}
        />
        {badge && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </span>
      {!collapsed && (
        <span className={cn("text-sm font-medium truncate", active ? "text-white" : "")}>
          {label}
        </span>
      )}
      {collapsed && (
        <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium bg-slate-900 text-white rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-lg border border-slate-700 transition-opacity">
          {label}
        </span>
      )}
    </Link>
  );
};

const MainListItems = ({ drawerClose, drawerOpen }) => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const { status: billing } = useBillingStatus();
  const [connectionWarning, setConnectionWarning] = useState(false);
  const collapsed = !drawerOpen;

  useEffect(() => {
    const t = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offline = whatsApps.filter((w) =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        );
        setConnectionWarning(offline.length > 0);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  // Only enforce per-item permissions once we actually know them. Users whose
  // role was never set up still see the full menu, exactly as before — hiding
  // everything from them would be a regression, not a fix.
  const enforce =
    Array.isArray(user.permissions) && user.permissions.length > 0;

  const Gate = ({ perform, children }) => {
    if (!enforce) return children;
    return (
      <Can
        role={user.profile}
        perform={perform}
        permissions={user.permissions}
        yes={() => children}
      />
    );
  };

  return (
    <nav className="flex flex-col gap-1 py-2" onClick={drawerClose}>
      <Gate perform="dashboard:view">
        <NavItem to="/" icon={LayoutDashboard} label="Dashboard" collapsed={collapsed} />
      </Gate>
      <Gate perform="connections:view">
        <NavItem
          to="/connections"
          icon={connectionWarning ? AlertTriangle : RefreshCw}
          label={i18n.t("mainDrawer.listItems.connections")}
          collapsed={collapsed}
          badge={connectionWarning}
        />
      </Gate>
      <Gate perform="tickets:view">
        <NavItem
          to="/tickets"
          icon={MessageSquare}
          label={i18n.t("mainDrawer.listItems.tickets")}
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="tickets:view">
        <NavItem to="/kanban" icon={Columns} label="Embudos" collapsed={collapsed} />
      </Gate>
      <Gate perform="statistics:view">
        <NavItem to="/statistics" icon={BarChart2} label="Estadísticas" collapsed={collapsed} />
      </Gate>
      <Gate perform="contacts:view">
        <NavItem
          to="/contacts"
          icon={Phone}
          label={i18n.t("mainDrawer.listItems.contacts")}
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="appointments:view">
        <NavItem
          to="/appointments"
          icon={CalendarDays}
          label={i18n.t("mainDrawer.listItems.appointments")}
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="billing:view">
        <NavItem
          to="/billing"
          icon={Receipt}
          label="Facturación"
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="quickAnswers:view">
        <NavItem
          to="/quickAnswers"
          icon={Zap}
          label={i18n.t("mainDrawer.listItems.quickAnswers")}
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="bot:view">
        <NavItem
          to="/bot"
          icon={Bot}
          label="Bot"
          collapsed={collapsed}
        />
      </Gate>
      <Gate perform="emailMarketing:view">
        <NavItem
          to="/email-marketing"
          icon={Mail}
          label="Email Marketing"
          collapsed={collapsed}
        />
      </Gate>

      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        permissions={user.permissions}
        yes={() => (
          <>
            <div className="mx-4 my-2 h-px bg-slate-800" />
            {!collapsed && (
              <p className="px-5 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {i18n.t("mainDrawer.listItems.administration")}
              </p>
            )}
            <NavItem
              to="/users"
              icon={Users}
              label={i18n.t("mainDrawer.listItems.users")}
              collapsed={collapsed}
            />
            <NavItem
              to="/queues"
              icon={GitFork}
              label={i18n.t("mainDrawer.listItems.queues")}
              collapsed={collapsed}
            />
            {user.profile === "admin" && (
              <>
                <NavItem
                  to="/Settings"
                  icon={Settings}
                  label={i18n.t("mainDrawer.listItems.settings")}
                  collapsed={collapsed}
                />
                <NavItem
                  to="/roles"
                  icon={Shield}
                  label="Roles"
                  collapsed={collapsed}
                />
                {/* Only the platform owner (BILLING_OWNER_EMAIL) sets the
                    prices; the customer's own admin must never see this. */}
                {billing?.isOwner && (
                  <>
                    <NavItem
                      to="/billing-settings"
                      icon={Wallet}
                      label="Cobros"
                      collapsed={collapsed}
                    />
                    <NavItem
                      to="/costs"
                      icon={TrendingUp}
                      label="Costos"
                      collapsed={collapsed}
                    />
                  </>
                )}
              </>
            )}
            <NavItem
              to="/audit-logs"
              icon={ScrollText}
              label="Auditoria"
              collapsed={collapsed}
            />
            <Can
              role={user.profile}
              perform="data:clean"
              permissions={user.permissions}
              yes={() => (
                <NavItem
                  to="/data-clean-manager"
                  icon={HardDrive}
                  label="Almacenamiento"
                  collapsed={collapsed}
                />
              )}
            />
          </>
        )}
      />
    </nav>
  );
};

export default MainListItems;
